/**
 * Weekly net down for one client (#418).
 *
 * Pure: dates are YYYY-MM-DD in the client's time zone, weights in the
 * result are pounds. Callers convert stored kilograms before passing them in.
 * https://github.com/jkoutavas/abbi-way/issues/418
 */
import { getEffectiveDayType, parseLocalDateString, } from './day-type-override.utils';
/** Runtime override. Ops can set this without a code change. */
export const WEEKLY_NET_DOWN_RULES_ENV = 'WEEKLY_NET_DOWN_RULES';
export const WEEKLY_NET_DOWN_CONFIG = {
    kgToLb: 2.20462,
    weightDecimals: 1,
    // Confirmed: more than 30 lb left → 2–4. 30 lb or less → 1–2,
    // including exactly 30 and near Ideal Weight. Bands are inclusive.
    goalBands: [
        { maxPoundsLeftInclusive: 30, minLb: 1, maxLb: 2 },
        { maxPoundsLeftInclusive: null, minLb: 2, maxLb: 4 },
    ],
    defaultGoalMinLb: 2,
    defaultGoalMaxLb: 4,
    overNetDownWarningLb: 4,
    exemptFirstProgramWeekFromOverWarning: true,
    monthlyBenchmarkLb: 10,
    // Configurable give-or-take around ~10 lb a month. Exactly 3 lb off
    // is not flagged. The number itself is still the open setting.
    monthlyFlagThresholdLb: 3,
    // Confirmed: first day of the week minus the last day of that week
    // (Monday to Sunday, or Sunday to Saturday). Not start-to-lowest.
    // The next week's start weigh-in is the net-down end, not gross loss.
    grossLossMethod: 'first-day-to-last-day',
    // Confirmed: one biometrics row per calendar day is that morning's
    // weigh-in (no clock time is stored). A miss uses the next day.
    boundaryWeighIn: 'calendar-day-then-next-days',
    boundaryFallbackDays: 1,
    monthlyAttribution: 'next-week-start',
    programStartEvent: 'earliest-activation',
    holdingAfterRewardDay: 'not-implemented',
    defaultTimeZone: 'America/New_York',
    maxWeeks: 520,
    assumptionsPendingAbbi: [
        {
            id: 'monthly-flag-threshold',
            summary: 'About 10 lb a month, give or take. The give-or-take is the monthlyFlagThresholdLb setting (currently 3 lb either way). Flag the month when its total is more than that far from 10. Exactly 3 lb off is not flagged. A week counts in the calendar month of the next week start, which is when that net down is known.',
        },
        {
            id: 'q5-holding-after-reward',
            summary: '#418 Q5 is still open and is not calculated (holdingAfterRewardDay is not-implemented). Moved or extra reward days do not change whether the week met net down (#418 Q6, answered).',
        },
        {
            id: 'program-start-is-first-activation',
            summary: 'Program start is the coach-entered start date on the earliest activation event (else that event time in the client time zone). A later reactivation does not move week 1. Set programStartEvent to latest-activation to use the newest one instead.',
        },
    ],
};
const GROSS_LOSS_METHODS = [
    'first-day-to-last-day',
];
const BOUNDARY_METHODS = [
    'calendar-day-then-next-days',
];
const MONTHLY_ATTRIBUTIONS = ['next-week-start'];
const PROGRAM_START_EVENTS = [
    'earliest-activation',
    'latest-activation',
];
const HOLDING_VALUES = ['not-implemented'];
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const MDY_DATE = /^(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])-(\d{4})$/;
const RESULT_LABEL = {
    met: 'on target',
    under: 'under',
    'flat-or-up': 'not down',
    over: 'over',
    'no-data': 'incomplete',
    'in-progress': 'in progress',
};
function isRecord(value) {
    return value != null && typeof value === 'object' && !Array.isArray(value);
}
function readFinite(value) {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
        return null;
    }
    return value;
}
export function roundWeight(value, decimals) {
    const factor = 10 ** decimals;
    return Math.round(value * factor) / factor;
}
export function kgToRoundedLb(kg, config = WEEKLY_NET_DOWN_CONFIG) {
    return roundWeight(kg * config.kgToLb, config.weightDecimals);
}
export function isIsoDate(value) {
    const match = ISO_DATE.exec(value);
    if (!match) {
        return false;
    }
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    if (month < 1 || month > 12 || day < 1 || day > 31) {
        return false;
    }
    const date = new Date(Date.UTC(year, month - 1, day));
    return (date.getUTCFullYear() === year &&
        date.getUTCMonth() === month - 1 &&
        date.getUTCDate() === day);
}
export function addIsoDays(isoDate, days) {
    const [year, month, day] = isoDate.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));
    date.setUTCDate(date.getUTCDate() + days);
    return date.toISOString().slice(0, 10);
}
function daysBetweenIso(start, end) {
    const [ys, ms, ds] = start.split('-').map(Number);
    const [ye, me, de] = end.split('-').map(Number);
    const a = Date.UTC(ys, ms - 1, ds);
    const b = Date.UTC(ye, me - 1, de);
    return Math.round((b - a) / 86400000);
}
export function weekdayIndex(isoDate) {
    const [year, month, day] = isoDate.split('-').map(Number);
    return new Date(Date.UTC(year, month - 1, day)).getUTCDay();
}
/** Week start (YYYY-MM-DD) on or before `isoDate`. weekStartsOn: 0 Sun, 1 Mon. */
export function weekStartOnOrBefore(isoDate, weekStartsOn) {
    const daysBack = (weekdayIndex(isoDate) - weekStartsOn + 7) % 7;
    return addIsoDays(isoDate, -daysBack);
}
/**
 * 1-based program week for the client week that contains `weekStart`.
 * Week 1 is the client week that contains the program start date when
 * `countWeekContainingProgramStart` is true (the same weekIndex the
 * history uses). A mid-week activation still belongs to that week.
 * When the flag is false, week 1 is the following week.
 * Returns null when the viewed week is before week 1, or the dates
 * are not real calendar days.
 */
export function programWeekNumber(args) {
    if (!isIsoDate(args.programStartDate) || !isIsoDate(args.weekStart)) {
        return null;
    }
    if (args.weekStartsOn !== 0 && args.weekStartsOn !== 1) {
        return null;
    }
    const first = weekStartOnOrBefore(args.programStartDate, args.weekStartsOn);
    const viewed = weekStartOnOrBefore(args.weekStart, args.weekStartsOn);
    const includeContaining = args.countWeekContainingProgramStart !== false;
    const origin = includeContaining ? first : addIsoDays(first, 7);
    if (viewed < origin) {
        return null;
    }
    return Math.floor(daysBetweenIso(origin, viewed) / 7) + 1;
}
/** Earliest activation is ASC. Latest activation is DESC. */
export function activationCreatedAtOrder(programStartEvent) {
    return programStartEvent === 'latest-activation' ? 'DESC' : 'ASC';
}
export function isValidTimeZone(timeZone) {
    try {
        new Intl.DateTimeFormat('en-US', { timeZone });
        return true;
    }
    catch {
        return false;
    }
}
export function resolveClientTimeZone(timeZone, fallback = WEEKLY_NET_DOWN_CONFIG.defaultTimeZone) {
    if (typeof timeZone === 'string' && isValidTimeZone(timeZone)) {
        return timeZone;
    }
    if (isValidTimeZone(fallback)) {
        return fallback;
    }
    return 'UTC';
}
/** Calendar YYYY-MM-DD for an instant in an IANA time zone. */
export function calendarDateInTimeZone(instant, timeZone) {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(instant);
    const year = parts.find((part) => part.type === 'year')?.value;
    const month = parts.find((part) => part.type === 'month')?.value;
    const day = parts.find((part) => part.type === 'day')?.value;
    if (!year || !month || !day) {
        throw new Error(`Unable to format a calendar date in ${timeZone}`);
    }
    return `${year}-${month}-${day}`;
}
export function parseActivationNote(note) {
    const empty = { startDate: null, weekStartsOn: null };
    if (!note || typeof note !== 'string') {
        return empty;
    }
    let parsed;
    try {
        parsed = JSON.parse(note);
    }
    catch {
        return empty;
    }
    if (!isRecord(parsed)) {
        return empty;
    }
    let startDate = null;
    if (typeof parsed.startDate === 'string') {
        const match = MDY_DATE.exec(parsed.startDate);
        if (match) {
            const iso = `${match[3]}-${match[1]}-${match[2]}`;
            if (isIsoDate(iso)) {
                startDate = iso;
            }
        }
    }
    const weekStartsOn = parsed.weekStartsOn === 0 || parsed.weekStartsOn === 1
        ? parsed.weekStartsOn
        : null;
    return { startDate, weekStartsOn };
}
export function resolveProgramStart(args) {
    const note = parseActivationNote(args.activationNote);
    if (note.startDate) {
        return { date: note.startDate, source: 'activation-note' };
    }
    if (args.activationCreatedAt == null || args.activationCreatedAt === '') {
        return null;
    }
    const instant = args.activationCreatedAt instanceof Date
        ? args.activationCreatedAt
        : new Date(args.activationCreatedAt);
    if (Number.isNaN(instant.getTime())) {
        return null;
    }
    return {
        date: calendarDateInTimeZone(instant, args.timeZone),
        source: 'activation-created-at',
    };
}
/**
 * Stored user.weekStartsOn wins. Otherwise the activation note, then the
 * program-start weekday when that day is Sunday or Monday, else Monday.
 * Does not write the user row. Meal Map keeps using the stored column.
 */
export function resolveNetDownWeekStartsOn(args) {
    if (args.stored === 0 || args.stored === 1) {
        return { weekStartsOn: args.stored, source: 'user' };
    }
    if (args.activationNoteWeekStartsOn === 0 ||
        args.activationNoteWeekStartsOn === 1) {
        return {
            weekStartsOn: args.activationNoteWeekStartsOn,
            source: 'activation-note',
        };
    }
    if (args.programStartDate && isIsoDate(args.programStartDate)) {
        const weekday = weekdayIndex(args.programStartDate);
        if (weekday === 0 || weekday === 1) {
            return { weekStartsOn: weekday, source: 'program-start-weekday' };
        }
    }
    return { weekStartsOn: 1, source: 'default-monday' };
}
function cloneConfig(config) {
    return {
        ...config,
        goalBands: config.goalBands.map((band) => ({ ...band })),
        assumptionsPendingAbbi: config.assumptionsPendingAbbi,
    };
}
function validateGoalBands(value) {
    if (!Array.isArray(value) || value.length === 0) {
        return 'goalBands must be a non-empty array';
    }
    const bands = [];
    let openBands = 0;
    for (const item of value) {
        if (!isRecord(item)) {
            return 'each goalBands entry must be an object';
        }
        const minLb = readFinite(item.minLb);
        const maxLb = readFinite(item.maxLb);
        if (minLb == null || maxLb == null || minLb > maxLb) {
            return 'goalBands minLb and maxLb must be numbers with minLb <= maxLb';
        }
        const maxPounds = item.maxPoundsLeftInclusive;
        if (maxPounds == null) {
            openBands += 1;
            bands.push({ maxPoundsLeftInclusive: null, minLb, maxLb });
            continue;
        }
        if (typeof maxPounds !== 'number' || !Number.isFinite(maxPounds)) {
            return 'goalBands maxPoundsLeftInclusive must be a number or null';
        }
        bands.push({ maxPoundsLeftInclusive: maxPounds, minLb, maxLb });
    }
    if (openBands > 1) {
        return 'goalBands can include only one open-ended band';
    }
    return bands;
}
function applyOverride(base, raw) {
    const next = cloneConfig(base);
    const assignNumber = (key, allowNegative) => {
        if (!(key in raw)) {
            return null;
        }
        const value = readFinite(raw[key]);
        if (value == null || (!allowNegative && value < 0)) {
            return `${key} must be a ${allowNegative ? 'finite' : 'non-negative'} number`;
        }
        next[key] = value;
        return null;
    };
    const numberError = assignNumber('kgToLb', false) ||
        assignNumber('defaultGoalMinLb', true) ||
        assignNumber('defaultGoalMaxLb', true) ||
        assignNumber('overNetDownWarningLb', false) ||
        assignNumber('monthlyBenchmarkLb', false) ||
        assignNumber('monthlyFlagThresholdLb', false);
    if (numberError) {
        return numberError;
    }
    if (next.defaultGoalMinLb > next.defaultGoalMaxLb) {
        return 'defaultGoalMinLb must be <= defaultGoalMaxLb';
    }
    if ('weightDecimals' in raw) {
        const value = raw.weightDecimals;
        if (typeof value !== 'number' ||
            !Number.isInteger(value) ||
            value < 0 ||
            value > 4) {
            return 'weightDecimals must be an integer from 0 to 4';
        }
        next.weightDecimals = value;
    }
    if ('boundaryFallbackDays' in raw) {
        const value = raw.boundaryFallbackDays;
        if (typeof value !== 'number' ||
            !Number.isInteger(value) ||
            value < 0 ||
            value > 6) {
            return 'boundaryFallbackDays must be an integer from 0 to 6';
        }
        next.boundaryFallbackDays = value;
    }
    if ('maxWeeks' in raw) {
        const value = raw.maxWeeks;
        if (typeof value !== 'number' ||
            !Number.isInteger(value) ||
            value < 1 ||
            value > 2000) {
            return 'maxWeeks must be an integer from 1 to 2000';
        }
        next.maxWeeks = value;
    }
    if ('exemptFirstProgramWeekFromOverWarning' in raw) {
        if (typeof raw.exemptFirstProgramWeekFromOverWarning !== 'boolean') {
            return 'exemptFirstProgramWeekFromOverWarning must be a boolean';
        }
        next.exemptFirstProgramWeekFromOverWarning =
            raw.exemptFirstProgramWeekFromOverWarning;
    }
    if ('goalBands' in raw) {
        const bands = validateGoalBands(raw.goalBands);
        if (typeof bands === 'string') {
            return bands;
        }
        next.goalBands = bands;
    }
    if ('grossLossMethod' in raw) {
        if (typeof raw.grossLossMethod !== 'string' ||
            !GROSS_LOSS_METHODS.includes(raw.grossLossMethod)) {
            return 'grossLossMethod is not a known method';
        }
        next.grossLossMethod = raw.grossLossMethod;
    }
    if ('boundaryWeighIn' in raw) {
        if (typeof raw.boundaryWeighIn !== 'string' ||
            !BOUNDARY_METHODS.includes(raw.boundaryWeighIn)) {
            return 'boundaryWeighIn is not a known method';
        }
        next.boundaryWeighIn = raw.boundaryWeighIn;
    }
    if ('monthlyAttribution' in raw) {
        if (typeof raw.monthlyAttribution !== 'string' ||
            !MONTHLY_ATTRIBUTIONS.includes(raw.monthlyAttribution)) {
            return 'monthlyAttribution is not a known method';
        }
        next.monthlyAttribution = raw.monthlyAttribution;
    }
    if ('programStartEvent' in raw) {
        if (typeof raw.programStartEvent !== 'string' ||
            !PROGRAM_START_EVENTS.includes(raw.programStartEvent)) {
            return 'programStartEvent must be earliest-activation or latest-activation';
        }
        next.programStartEvent = raw.programStartEvent;
    }
    if ('holdingAfterRewardDay' in raw) {
        if (typeof raw.holdingAfterRewardDay !== 'string' ||
            !HOLDING_VALUES.includes(raw.holdingAfterRewardDay)) {
            return 'holdingAfterRewardDay cannot be changed until #418 Q5 is defined';
        }
        next.holdingAfterRewardDay =
            raw.holdingAfterRewardDay;
    }
    if ('defaultTimeZone' in raw) {
        if (typeof raw.defaultTimeZone !== 'string' ||
            !isValidTimeZone(raw.defaultTimeZone)) {
            return 'defaultTimeZone must be an IANA time zone';
        }
        next.defaultTimeZone = raw.defaultTimeZone;
    }
    return next;
}
/**
 * Merge a JSON object over the code defaults. Invalid JSON or an invalid
 * field keeps the defaults and returns an error string. The pending-Abbi
 * list always stays the one shipped in code.
 */
export function resolveWeeklyNetDownConfig(overrideJson) {
    if (overrideJson == null || overrideJson.trim() === '') {
        return {
            config: WEEKLY_NET_DOWN_CONFIG,
            source: 'defaults',
            error: null,
        };
    }
    let parsed;
    try {
        parsed = JSON.parse(overrideJson);
    }
    catch {
        return {
            config: WEEKLY_NET_DOWN_CONFIG,
            source: 'defaults',
            error: `${WEEKLY_NET_DOWN_RULES_ENV} is not valid JSON`,
        };
    }
    if (!isRecord(parsed)) {
        return {
            config: WEEKLY_NET_DOWN_CONFIG,
            source: 'defaults',
            error: `${WEEKLY_NET_DOWN_RULES_ENV} must be a JSON object`,
        };
    }
    const applied = applyOverride(WEEKLY_NET_DOWN_CONFIG, parsed);
    if (typeof applied === 'string') {
        return {
            config: WEEKLY_NET_DOWN_CONFIG,
            source: 'defaults',
            error: applied,
        };
    }
    return { config: applied, source: 'override', error: null };
}
export function goalRangeForPoundsLeft(poundsLeft, config) {
    if (poundsLeft == null || !Number.isFinite(poundsLeft)) {
        return {
            minLb: config.defaultGoalMinLb,
            maxLb: config.defaultGoalMaxLb,
            source: 'default',
        };
    }
    const bands = [...config.goalBands].sort((a, b) => {
        if (a.maxPoundsLeftInclusive == null) {
            return 1;
        }
        if (b.maxPoundsLeftInclusive == null) {
            return -1;
        }
        return a.maxPoundsLeftInclusive - b.maxPoundsLeftInclusive;
    });
    for (const band of bands) {
        if (band.maxPoundsLeftInclusive == null ||
            poundsLeft <= band.maxPoundsLeftInclusive) {
            return { minLb: band.minLb, maxLb: band.maxLb, source: 'pounds-left' };
        }
    }
    return {
        minLb: config.defaultGoalMinLb,
        maxLb: config.defaultGoalMaxLb,
        source: 'default',
    };
}
function rulesView(config, source, error) {
    return {
        source,
        error,
        goalBands: config.goalBands.map((band) => ({ ...band })),
        defaultGoalMinLb: config.defaultGoalMinLb,
        defaultGoalMaxLb: config.defaultGoalMaxLb,
        overNetDownWarningLb: config.overNetDownWarningLb,
        exemptFirstProgramWeekFromOverWarning: config.exemptFirstProgramWeekFromOverWarning,
        monthlyBenchmarkLb: config.monthlyBenchmarkLb,
        monthlyFlagThresholdLb: config.monthlyFlagThresholdLb,
        grossLossMethod: config.grossLossMethod,
        boundaryWeighIn: config.boundaryWeighIn,
        boundaryFallbackDays: config.boundaryFallbackDays,
        monthlyAttribution: config.monthlyAttribution,
        programStartEvent: config.programStartEvent,
        holdingAfterRewardDay: config.holdingAfterRewardDay,
    };
}
function findBoundaryWeight(weights, boundaryDate, lastDateInclusive) {
    if (lastDateInclusive < boundaryDate) {
        return null;
    }
    let cursor = boundaryDate;
    let steps = 0;
    while (cursor <= lastDateInclusive) {
        const lb = weights[cursor];
        if (typeof lb === 'number' && Number.isFinite(lb) && lb > 0) {
            return { date: cursor, lb, usedFallback: steps > 0 };
        }
        cursor = addIsoDays(cursor, 1);
        steps += 1;
    }
    return null;
}
function weightOnDate(weights, date) {
    const lb = weights[date];
    if (typeof lb === 'number' && Number.isFinite(lb) && lb > 0) {
        return lb;
    }
    return null;
}
/**
 * Latest weigh-in on the days of this week up through today, strictly
 * after the week-start weigh-in. The running net uses this. Days outside
 * the week, future days, and the start day itself are ignored. A missed
 * boundary's next-day substitute is not applied here.
 */
function latestWeighInAfterStart(weightsLbByDate, startDate, weekStart, weekEnd, today) {
    if (!isIsoDate(startDate) ||
        !isIsoDate(weekStart) ||
        !isIsoDate(weekEnd) ||
        !isIsoDate(today)) {
        return null;
    }
    const through = minIso(weekEnd, today);
    const firstDay = addIsoDays(startDate, 1);
    if (through < firstDay || through < weekStart) {
        return null;
    }
    let latest = null;
    const begin = firstDay > weekStart ? firstDay : weekStart;
    for (let cursor = begin; cursor <= through; cursor = addIsoDays(cursor, 1)) {
        const lb = weightOnDate(weightsLbByDate, cursor);
        if (lb == null) {
            continue;
        }
        latest = { date: cursor, lb };
    }
    return latest;
}
/**
 * Lowest weigh-in on the days of this week up through today.
 * Informational only. Ties keep the earlier day. A later day in the
 * same week is ignored until that day has arrived. Days outside the
 * week are ignored.
 */
export function lowestWeighInDate(weightsLbByDate, weekStart, weekEnd, today) {
    if (!isIsoDate(weekStart) || !isIsoDate(weekEnd) || !isIsoDate(today)) {
        return null;
    }
    const through = minIso(weekEnd, today);
    if (through < weekStart) {
        return null;
    }
    let lowest = null;
    for (let cursor = weekStart; cursor <= through; cursor = addIsoDays(cursor, 1)) {
        const lb = weightOnDate(weightsLbByDate, cursor);
        if (lb == null) {
            continue;
        }
        if (lowest == null || lb < lowest.lb) {
            lowest = { date: cursor, lb };
        }
    }
    return lowest;
}
function grossLossForWeek(method, weekStart, weekEnd, today, weights, decimals) {
    // Isolated on purpose. Add a method here rather than inlining a
    // different formula into week assembly.
    const lowest = lowestWeighInDate(weights, weekStart, weekEnd, today);
    const startLb = weightOnDate(weights, weekStart);
    const weekComplete = today >= weekEnd;
    const endLb = weekComplete ? weightOnDate(weights, weekEnd) : null;
    const grossLossLb = method === 'first-day-to-last-day' && startLb != null && endLb != null
        ? roundWeight(startLb - endLb, decimals)
        : null;
    return {
        grossLossLb,
        grossStartWeightLb: startLb,
        grossStartWeightDate: startLb == null ? null : weekStart,
        grossEndWeightLb: endLb,
        grossEndWeightDate: endLb == null ? null : weekEnd,
        lowestWeightLb: lowest?.lb ?? null,
        lowestWeightDate: lowest?.date ?? null,
    };
}
function rewardDaysForWeek(weekStart, programType, weekStartsOn, overrides) {
    const days = [];
    for (let i = 0; i < 7; i += 1) {
        const date = addIsoDays(weekStart, i);
        const local = parseLocalDateString(date);
        if (!local) {
            continue;
        }
        const dayType = getEffectiveDayType(programType, local, overrides[date], {
            weekStartsOn,
        });
        if (dayType === 'reward') {
            days.push(date);
        }
    }
    return days;
}
function statusForNet(net, goalMin, goalMax) {
    if (net <= 0) {
        return 'flat-or-up';
    }
    if (net < goalMin) {
        return 'under';
    }
    if (net > goalMax) {
        return 'over';
    }
    return 'met';
}
function minIso(a, b) {
    return a <= b ? a : b;
}
function emptyHistory(input, config) {
    return {
        timeZone: input.timeZone,
        today: input.today,
        weekStartsOn: input.weekStartsOn,
        weekStartsOnSource: input.weekStartsOnSource,
        programType: input.programType,
        programStartDate: input.programStartDate,
        programStartSource: input.programStartSource,
        idealWeightLb: input.idealWeightLb,
        firstProgramWeekKnown: input.programStartDate != null,
        truncated: false,
        assumptionsPendingAbbi: config.assumptionsPendingAbbi,
        rules: rulesView(config, input.rulesSource ?? 'defaults', input.rulesError ?? null),
        weeks: [],
        months: [],
    };
}
export function buildWeeklyNetDownHistory(input) {
    const config = input.config ?? WEEKLY_NET_DOWN_CONFIG;
    if (!isIsoDate(input.today)) {
        throw new Error(`today must be YYYY-MM-DD, got ${input.today}`);
    }
    const programStartDate = input.programStartDate;
    if (programStartDate == null || !isIsoDate(programStartDate)) {
        return emptyHistory(input, config);
    }
    const first = weekStartOnOrBefore(programStartDate, input.weekStartsOn);
    const last = weekStartOnOrBefore(input.today, input.weekStartsOn);
    if (first > last) {
        return emptyHistory(input, config);
    }
    const spanWeeks = Math.floor(daysBetweenIso(first, last) / 7) + 1;
    let startCursor = first;
    let truncated = false;
    if (spanWeeks > config.maxWeeks) {
        truncated = true;
        startCursor = addIsoDays(last, -7 * (config.maxWeeks - 1));
    }
    const weekStarts = [];
    for (let cursor = startCursor; cursor <= last; cursor = addIsoDays(cursor, 7)) {
        weekStarts.push(cursor);
    }
    const firstIndex = spanWeeks - weekStarts.length + 1;
    const fallbackDays = Math.max(0, Math.min(6, config.boundaryFallbackDays));
    const decimals = config.weightDecimals;
    const weeksChronological = [];
    weekStarts.forEach((weekStart, offset) => {
        const weekIndex = programWeekNumber({
            programStartDate,
            weekStartsOn: input.weekStartsOn,
            weekStart,
        }) ?? firstIndex + offset;
        const weekEnd = addIsoDays(weekStart, 6);
        const nextWeekStart = addIsoDays(weekStart, 7);
        const isFirstProgramWeek = weekIndex === 1;
        const startWindowLast = addIsoDays(weekStart, fallbackDays);
        const endWindowLast = addIsoDays(nextWeekStart, fallbackDays);
        const nextWeekHasStarted = input.today >= nextWeekStart;
        const startSearchEnd = minIso(startWindowLast, input.today);
        const start = findBoundaryWeight(input.weightsLbByDate, weekStart, startSearchEnd);
        const end = nextWeekHasStarted
            ? findBoundaryWeight(input.weightsLbByDate, nextWeekStart, minIso(endWindowLast, input.today))
            : null;
        const gross = grossLossForWeek(config.grossLossMethod, weekStart, weekEnd, input.today, input.weightsLbByDate, decimals);
        const poundsLeft = start == null ||
            input.idealWeightLb == null ||
            !Number.isFinite(input.idealWeightLb) ||
            input.idealWeightLb <= 0
            ? null
            : roundWeight(start.lb - input.idealWeightLb, decimals);
        const goal = goalRangeForPoundsLeft(poundsLeft, config);
        let status;
        let incompleteReason = null;
        let netChangeLb = null;
        if (!nextWeekHasStarted) {
            status = 'in-progress';
            incompleteReason = start
                ? 'awaiting-next-week'
                : 'awaiting-start-weigh-in';
        }
        else if (!start) {
            status = 'no-data';
            incompleteReason = 'missing-start-weigh-in';
        }
        else if (!end) {
            const endWindowClosed = input.today > endWindowLast;
            if (endWindowClosed) {
                status = 'no-data';
                incompleteReason = 'missing-end-weigh-in';
            }
            else {
                status = 'in-progress';
                incompleteReason = 'awaiting-end-weigh-in';
            }
        }
        else {
            netChangeLb = roundWeight(start.lb - end.lb, decimals);
            status = statusForNet(netChangeLb, goal.minLb, goal.maxLb);
        }
        const checkInWarning = netChangeLb != null &&
            netChangeLb > config.overNetDownWarningLb &&
            !(config.exemptFirstProgramWeekFromOverWarning && isFirstProgramWeek);
        // Open weeks only. A later in-week weigh-in is not the next week's
        // morning, so it never fills netChangeLb, the month, or the check-in.
        let provisionalNetChangeLb = null;
        let provisionalEndWeightLb = null;
        let provisionalEndWeightDate = null;
        if (status === 'in-progress' && start) {
            const latest = latestWeighInAfterStart(input.weightsLbByDate, start.date, weekStart, weekEnd, input.today);
            if (latest) {
                provisionalEndWeightLb = latest.lb;
                provisionalEndWeightDate = latest.date;
                provisionalNetChangeLb = roundWeight(start.lb - latest.lb, decimals);
            }
        }
        weeksChronological.push({
            weekIndex,
            isFirstProgramWeek,
            weekStart,
            weekEnd,
            nextWeekStart,
            startWeightLb: start?.lb ?? null,
            startWeightDate: start?.date ?? null,
            startWeightUsedFallback: start?.usedFallback ?? false,
            endWeightLb: end?.lb ?? null,
            endWeightDate: end?.date ?? null,
            endWeightUsedFallback: end?.usedFallback ?? false,
            netChangeLb,
            provisionalNetChangeLb,
            provisionalEndWeightDate,
            provisionalEndWeightLb,
            grossLossLb: gross.grossLossLb,
            grossStartWeightLb: gross.grossStartWeightLb,
            grossStartWeightDate: gross.grossStartWeightDate,
            grossEndWeightLb: gross.grossEndWeightLb,
            grossEndWeightDate: gross.grossEndWeightDate,
            lowestWeightLb: gross.lowestWeightLb,
            lowestWeightDate: gross.lowestWeightDate,
            poundsLeftToLose: poundsLeft,
            goalMinLb: goal.minLb,
            goalMaxLb: goal.maxLb,
            goalBandSource: goal.source,
            status,
            resultLabel: RESULT_LABEL[status],
            checkInWarning,
            incomplete: status === 'no-data' || status === 'in-progress',
            incompleteReason,
            rewardDays: rewardDaysForWeek(weekStart, input.programType || '421', input.weekStartsOn, input.dayTypeOverrides),
        });
    });
    const months = buildMonths(weeksChronological, config);
    return {
        ...emptyHistory(input, config),
        firstProgramWeekKnown: true,
        truncated,
        weeks: [...weeksChronological].reverse(),
        months,
    };
}
function monthKey(week, config) {
    switch (config.monthlyAttribution) {
        case 'next-week-start':
            return week.nextWeekStart.slice(0, 7);
        default: {
            const unknown = config.monthlyAttribution;
            return unknown;
        }
    }
}
function buildMonths(weeks, config) {
    const byMonth = new Map();
    for (const week of weeks) {
        const month = monthKey(week, config);
        const bucket = byMonth.get(month) ?? { net: 0, counted: 0, missing: 0 };
        if (week.netChangeLb == null) {
            bucket.missing += 1;
        }
        else {
            bucket.net += week.netChangeLb;
            bucket.counted += 1;
        }
        byMonth.set(month, bucket);
    }
    const months = [];
    for (const [month, bucket] of byMonth) {
        const netDownLb = bucket.counted === 0
            ? null
            : roundWeight(bucket.net, config.weightDecimals);
        const delta = netDownLb == null
            ? null
            : roundWeight(netDownLb - config.monthlyBenchmarkLb, config.weightDecimals);
        const flag = delta != null && Math.abs(delta) > config.monthlyFlagThresholdLb;
        months.push({
            month,
            netDownLb,
            benchmarkLb: config.monthlyBenchmarkLb,
            deltaFromBenchmarkLb: delta,
            flag,
            partial: bucket.missing > 0,
            weeksCounted: bucket.counted,
            weeksWithoutNet: bucket.missing,
        });
    }
    months.sort((a, b) => {
        if (a.month === b.month) {
            return 0;
        }
        return a.month < b.month ? 1 : -1;
    });
    return months;
}
